import {
  MemoInterface,
  WriterInterface,
  TopicInterface,
  BoardsInterface as CollectionsInterface,
} from "@carglassgithub/memod-sdk";
import Loading from "@components/atoms/loading";
import InputSearch from "@components/molecules/input-search";
import MemoCard from "@components/organisms/memo-card-v2";
import CollectionCard from "@components/organisms/collection-card-v2";
import NavSearch from "@components/molecules/nav-search";
import ProfileOptions from "@components/molecules/profile-options";
import SearchMessage from "@components/molecules/search-message";
import CommonHeader from "@components/organisms/common-header";
import TopicCard from "@components/organisms/topic-card";
import CommonLayout from "@components/templates/common-layout";
import {
  searchCategories,
  searchRecommendationCategories,
} from "@models/api/search";
import { ssrInjector } from "@models/interactions/ssr-injector";
import classNames from "classnames";
import { GetServerSideProps } from "next";
import { useEffect, useState } from "react";
import { useStore } from "jotai";
import { AUTH_STATUS, AuthModalAtom } from "@models/state/atoms/auth-modal.atom";
import { useRouter } from "next/router";

interface Props {
  isLogged: boolean;
  query: string;
  enpoint: string;
}

export function useSearch(props: Props) {
  const router = useRouter();
  const store = useStore();

  const [search, setSearch] = useState(props.query);
  const [index, setIndex] = useState<string>(props.enpoint);
  const [data, setData] = useState([]);
  const [message, setMessage] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAuthModal, toggleAuthModal] = useState<boolean>(false);

  async function searchData() {
    setData([]);
    setIsLoading(true);
    setMessage(false);
    try {
      const response = await searchCategories(search, index);
      if (!response.length) {
        const response = await searchRecommendationCategories(index);
        setData(response);
        setIsLoading(false);
        setMessage(true);
        return;
      }
      setData(response);
      setIsLoading(false);
    } catch (e) {
      console.log(e);
    }
  }

  useEffect(() => {
    searchData();
  }, [index]);

  useEffect(() => {
    if (!router?.query?.slug) return;

    setIndex(router.query.slug[1]);
  }, [router.query.slug]);

  useEffect(() => {
    const handleRouteChange = () => {
      setData([]);
      setIsLoading(true);
    }

    router.events.on("routeChangeStart", handleRouteChange);

    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    }
  }, []);

  const handleLogin = () => {
    store.set(AuthModalAtom, {
      mode: AUTH_STATUS.LOGIN,
      open: true,
    });
  }

  return {
    models: {
      data,
      index,
      search,
      message,
      isLoading,
      showAuthModal,
    },
    operations: {
      setIndex,
      setSearch,
      setIsLoading,
      toggleAuthModal,
      searchData,
      handleLogin,
    },
  };
}

export default function Search(props: Props) {
  const { models, operations } = useSearch(props);

  return (
    <CommonLayout
      Header={<CommonHeader />}
      className="flex w-full !flex-col !items-center !justify-start gap-10"
    >
      <main className="mt-[40px] flex w-full flex-col items-center justify-center gap-10 self-center p-0 lg:w-[948px] ">
        <InputSearch
          search={models.search}
          setSearch={operations.setSearch}
          index={models.index}
          autocomplete={true}
          onEnter={operations.searchData}
        />
        <NavSearch
          index={models.index}
          setIndex={operations.setIndex}
          setIsLoading={operations.setIsLoading}
          query={props.query}
          search={models.search}
        />

        {models.message && <SearchMessage search={models.search} />}

        {models.index === "memos" && !models.isLoading && (
          <div className="md:w-[800px] lg:w-[896px] px-4 md:px-0">
            <div className="flex flex-col md:max-w-[600px] pb-4">
              {models.data.map((memo: MemoInterface) => (
                <MemoCard
                  key={`memo-${memo.id}`}
                  memo={memo}
                  variant="detailed"
                />
              ))}
            </div>
          </div>
        )}
        {models.index === "users" && !models.isLoading && (
          <section className="max-auto max-w-screen-[592px]:flex max-w-screen-[592px]:justify-center w-full columns-1 items-center gap-3 gap-x-3 space-y-3 pb-4 sm:w-[592px] sm:columns-2 lg:w-[888px] lg:columns-3">
            {models.data?.map((writer: WriterInterface) => {
              return (
                <div
                  key={writer?.id}
                  className="flex justify-center sm:break-inside-avoid"
                >
                  <ProfileOptions {...models} writer={writer} size="small" />
                </div>
              );
            })}
          </section>
        )}
        {models.index === "topics" && !models.isLoading && (
          <section className="max-auto max-w-screen-[592px]:flex max-w-screen-[592px]:justify-center w-full columns-1 gap-3 gap-x-3 space-y-3 pb-4 sm:w-[592px] sm:columns-2 lg:w-[888px] lg:columns-3">
            {models.data?.map((topic: TopicInterface) => {
              return (
                <div
                  key={topic.id}
                  className="flex justify-center sm:break-inside-avoid"
                >
                  <TopicCard {...models} topic={topic} />
                </div>
              );
            })}
          </section>
        )}
        {models.index === "boards" && !models.isLoading && (
          <div className="w-full md:w-[800px] lg:w-[896px] px-4 md:px-0 mt-4">
            <div className="flex flex-col gap-2 md:max-w-[600px]">
              {models.data.map((collection: CollectionsInterface) => (
                <CollectionCard
                  key={`collection-${collection.id}`}
                  collection={collection}
                  variant="detailed"
                  isUserLoggedIn={props.isLogged}
                  handleLogin={operations.handleLogin}
                />
              ))}
            </div>
          </div>
        )}
        <Loading
          size={90}
          className={classNames({ hidden: !models.isLoading }, "mx-auto")}
        />
      </main>
    </CommonLayout>
  );
}

export const getServerSideProps: GetServerSideProps = async ({
  params,
  res,
  ...context
}) => {
  try {
    const { slug } = params!;
    const isLogged = Boolean(ssrInjector(context));

    return {
      props: {
        isLogged: isLogged ?? null,
        query: slug![0],
        enpoint: slug![1],
      },
    };
  } catch (e) {
    console.log(e);
    return {
      redirect: {
        destination: "/",
        permanent: false,
      },
    };
  }
};
